import { useEffect, useState } from "react";
import { affiliateEarnings, registerAffiliate } from "../lib/api";

const STORAGE_KEY = "shadow-court.affiliate.userId";
const REFCODE_STORAGE_KEY = "shadow-court.affiliate.refCode";

export default function AffiliatePortal() {
  const [userId, setUserId] = useState<string | null>(() =>
    // Use sessionStorage to survive refresh; fine for a prototype — move to proper auth later.
    typeof window !== "undefined" ? window.sessionStorage.getItem(STORAGE_KEY) : null
  );
  const [refCode, setRefCode] = useState<string | null>(() =>
    typeof window !== "undefined" ? window.sessionStorage.getItem(REFCODE_STORAGE_KEY) : null
  );
  const [earnings, setEarnings] = useState<Awaited<ReturnType<typeof affiliateEarnings>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    affiliateEarnings(userId)
      .then(setEarnings)
      .catch((err) => setError(String(err)));
  }, [userId]);

  if (!userId) {
    return <Signup onRegistered={(u, rc) => {
      window.sessionStorage.setItem(STORAGE_KEY, u);
      window.sessionStorage.setItem(REFCODE_STORAGE_KEY, rc);
      setUserId(u);
      setRefCode(rc);
    }} loading={loading} setLoading={setLoading} error={error} setError={setError} />;
  }

  const refLink = refCode ? `https://shadow-court.pages.dev?ref=${refCode}` : "";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold">Affiliate Portal</h1>
        <p className="text-slate-400 mt-1">Verdiene an jedem Fall, den jemand aus Deiner Chain einreicht.</p>
      </header>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <h2 className="text-sm uppercase tracking-wider text-slate-400">Dein Affiliate-Link</h2>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 rounded-lg bg-slate-950 border border-slate-700 px-3 py-2 text-sm font-mono break-all">
            {refLink}
          </code>
          <button
            onClick={() => navigator.clipboard.writeText(refLink)}
            className="rounded-lg bg-court-600 hover:bg-court-500 px-4 py-2 text-sm font-semibold transition"
          >
            Kopieren
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Ref-Code: <span className="font-mono text-court-300">{refCode}</span>
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Ausbezahlt" value={earnings ? `${earnings.paidEur.toFixed(2)} EUR` : "—"} accent="emerald" />
        <Stat label="Ausstehend" value={earnings ? `${earnings.pendingEur.toFixed(2)} EUR` : "—"} accent="amber" />
        <Stat label="Provisionen" value={earnings ? String(earnings.commissionCount) : "—"} accent="court" />
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        <h2 className="text-sm uppercase tracking-wider text-slate-400 mb-4">Aufschlüsselung nach Tier</h2>
        {earnings?.byTier?.length ? (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-800">
              <tr>
                <th className="py-2">Tier</th>
                <th>Anzahl</th>
                <th className="text-right">Summe</th>
              </tr>
            </thead>
            <tbody>
              {earnings.byTier.map((row) => (
                <tr key={row.tier} className="border-b border-slate-800/60">
                  <td className="py-2 font-semibold text-court-300">L{row.tier}</td>
                  <td>{row.n}</td>
                  <td className="text-right font-mono">{(row.total_cents / 100).toFixed(2)} EUR</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500 italic">Noch keine Provisionen. Teile Deinen Link und lege los.</p>
        )}
      </div>

      <details className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-400">
        <summary className="cursor-pointer font-semibold text-slate-300">
          Rechtlicher Hinweis zum Affiliate-Modell
        </summary>
        <p className="mt-3 leading-relaxed">
          Shadow Court zahlt Provisionen ausschließlich für tatsächlich getätigte
          Produktkäufe von Dritten — nicht für die Anwerbung weiterer Affiliates.
          Es gibt keine Einstiegsgebühr. Das Modell ist kein Schneeballsystem
          im Sinne § 16 Abs. 2 UWG. Affiliates dürfen keine Rechtsberatung erbringen (RDG).
        </p>
      </details>
    </div>
  );
}

function Signup({
  onRegistered,
  loading,
  setLoading,
  error,
  setError,
}: {
  onRegistered: (userId: string, refCode: string) => void;
  loading: boolean;
  setLoading: (b: boolean) => void;
  error: string | null;
  setError: (s: string | null) => void;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [refCode, setRefCode] = useState(
    new URLSearchParams(window.location.search).get("ref") ?? ""
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { userId, refCode: myCode } = await registerAffiliate({
        email,
        displayName,
        refCode: refCode || undefined,
      });
      onRegistered(userId, myCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md mx-auto space-y-4">
      <h1 className="text-3xl font-bold">Als Affiliate anmelden</h1>
      <p className="text-sm text-slate-400">
        25 % Provision auf direkt referrierte Kunden, 10 % auf L2, 5 % auf L3.
      </p>
      <input
        type="email"
        required
        placeholder="E-Mail"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
      />
      <input
        type="text"
        required
        minLength={2}
        placeholder="Anzeigename"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-court-500"
      />
      <input
        type="text"
        placeholder="Ref-Code (optional, falls Du geworben wurdest)"
        value={refCode}
        onChange={(e) => setRefCode(e.target.value.toUpperCase())}
        className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-court-500"
      />
      {error && (
        <div className="rounded-lg bg-red-950/50 border border-red-800 px-4 py-2 text-sm text-red-200">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg bg-gradient-to-r from-court-500 to-court-700 hover:from-court-400 hover:to-court-600 px-4 py-3 font-semibold transition shadow-lg shadow-court-700/30 disabled:opacity-50"
      >
        {loading ? "Erstelle Konto…" : "Konto erstellen"}
      </button>
    </form>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "emerald" | "amber" | "court";
}) {
  const accentMap = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    court: "text-court-300",
  };
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
      <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-2 text-3xl font-mono font-bold ${accentMap[accent]}`}>{value}</p>
    </div>
  );
}
